/** @jsx jsx */
import React, { useState, useEffect } from "react";
import { jsx } from "@emotion/core";
import { Route, Redirect, Switch } from "react-router-dom";
import _find from "lodash/find";
import _get from "lodash/get";

import { resourceUrls } from "../config";

import ErrorBoundary from "../utils/ErrorBoundary";
import Layout from "../components/Layout";
import Header from "./components/Header";

import Intro from "./Intro";
import CurrentStudy from "./CurrentStudy";
import AdditionalQualifications from "./AdditionalQualifications";
import CodingLanguages from "./CodingLanguages";
import WorkEligibility from "./WorkEligibility";
import HealthDeclaration from "./HealthDeclaration";
import PersonalContact from "./PersonalContact";
import CertificateUpload from "./CertificateUpload";
import EvaluatingApplication from "./EvaluatingApplication";
import TutoringSubjects from "./TutoringSubjects";
import Qualifications from "./Qualifications";
import GCSE from "./GCSE";
import Submit from "./Submit";
import ReviewingApplication from "./ReviewingApplication";
import InterviewNext from "./InterviewNext";
import PageContainer from "./components/PageContainer";
import { handleGet } from "./api/handler";
import { get } from "./api";
import Error from "../components/Error";
import PrimaryCTA from "../components/PrimaryCTA";
import SecondaryCTA from "../components/SecondaryCTA";

const getCanApply = async (
    setIsLoading,
    setIsLoggedIn,
    setError,
    setCanApply
) => {
    await handleGet({
        getApiCall: () => get({ url: resourceUrls.apply.getCanApply }),
        setIsLoading,
        setIsLoggedIn,
        setError,
        setData: setCanApply,
    });
};

const getNextStep = async (setIsLoading, setIsLoggedIn, setError, history) => {
    const response = await handleGet({
        getApiCall: () => get({ url: resourceUrls.apply.nextStep }),
        setIsLoading,
        setIsLoggedIn,
        setError,
    });

    if (_get(response, "success")) {
        let nextStepFromResponse = _get(response, "success.data");
        history.push(stages[nextStepFromResponse].path);
    }
};

export const stages = {
    INTRO: {
        position: 1,
        path: "/secure/apply/tr-intro",
    },
    MOTIVATION: {
        position: 0,
        path: "",
    },
    DEGREE: {
        position: 2,
        path: "/secure/apply/tr-currentstudy",
    },
    SCHOOL_QUALIFICATION: {
        position: 3,
        path: "/secure/apply/tr-qualifications",
    },
    GCSE_QUALIFICATION: {
        position: 3.5,
        path: "/secure/apply/tr-gcse",
    },
    ADDITIONAL_QUALIFICATION: {
        position: 4,
        path: "/secure/apply/tr-additionalqualifications",
    },
    LANGUAGE: {
        position: 0,
        path: "",
    },
    CODING_LANGUAGE: {
        position: 5,
        path: "/secure/apply/tr-codinglanguage",
    },
    ELIGIBILITY: {
        position: 6,
        path: "/secure/apply/tr-eligibility",
    },
    HEALTH_DECLARATION: {
        position: 6.5,
        path: "/secure/apply/tr-healthdeclaration",
    },
    PERSONAL_CONTACT: {
        position: 7,
        path: "/secure/apply/tr-personalcontact",
    },
    HEARD_ABOUT: {
        position: 0,
        path: "",
    },
    DEGREE_CERTIFICATE: {
        position: 8,
        path: "/secure/apply/tr-certification",
    },
    SUBMIT: {
        position: 8,
        path: "/secure/apply/tr-submit",
    },
    TUTORING_SUBJECT: {
        position: 8,
        path: "/secure/apply/tr-tutoringsubjects",
    },
    AWAITING_INTERVIEW: {
        position: 9,
        path: "/secure/apply/tr-interview-next",
    },
    UNDER_REVIEW: {
        position: 9,
        path: "/secure/apply/tr-complete",
    },
    AWAITING_INFORMATION: {
        position: 9,
        path: "/secure/apply/tr-complete",
    },
    WAITING_LIST: {
        position: 9,
        path: "/secure/apply/tr-complete",
    },
};

const CanApplyContainer = ({ history, location }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [error, setError] = useState(null);
    const [canApply, setCanApply] = useState(null);

    useEffect(() => {
        getCanApply(setIsLoading, setIsLoggedIn, setError, setCanApply);
    }, [location.pathname]);

    return (
        <PageContainer
            isLoading={isLoading}
            isLoggedIn={isLoggedIn}
            error={error}
            canApply={canApply}
        >
            <NextStepContainer history={history} location={location} />
        </PageContainer>
    );
};

const NextStepContainer = ({ history, location }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        getNextStep(setIsLoading, setIsLoggedIn, setError, history);
    }, [location.pathname]);

    const currentStageNumber = _get(
        _find(stages, ["path", location.pathname]),
        "position"
    );

    return (
        <PageContainer
            isLoading={isLoading}
            isLoggedIn={isLoggedIn}
            error={error}
        >
            <Apply currentStageNumber={currentStageNumber} />
        </PageContainer>
    );
};

const Apply = ({ currentStageNumber }) => {
    return (
        <>
            <Header
                title="Application"
                currentStage={currentStageNumber}
                numberOfStages={9}
            />
            <Layout spacing={8}>
                <ErrorBoundary>
                    <Switch>
                        <Route path={stages["INTRO"].path} component={Intro} />
                        <Route
                            path={stages["DEGREE"].path}
                            render={props => <CurrentStudy {...props} />}
                        />
                        <Route
                            path={stages["SCHOOL_QUALIFICATION"].path}
                            component={Qualifications}
                        />
                        <Route
                            path={stages["GCSE_QUALIFICATION"].path}
                            component={GCSE}
                        />
                        <Route
                            path={stages["ADDITIONAL_QUALIFICATION"].path}
                            component={AdditionalQualifications}
                        />
                        <Route
                            path={stages["CODING_LANGUAGE"].path}
                            component={CodingLanguages}
                        />
                        <Route
                            path={stages["ELIGIBILITY"].path}
                            component={WorkEligibility}
                        />
                        <Route
                            path={stages["HEALTH_DECLARATION"].path}
                            component={HealthDeclaration}
                        />
                        <Route
                            path={stages["PERSONAL_CONTACT"].path}
                            component={PersonalContact}
                        />
                        <Route
                            path={stages["DEGREE_CERTIFICATE"].path}
                            component={CertificateUpload}
                        />
                        <Route
                            path="/secure/apply/tr-evaluatingapplication"
                            component={EvaluatingApplication}
                        />
                        <Route
                            path={stages["TUTORING_SUBJECT"].path}
                            component={TutoringSubjects}
                        />
                        <Route
                            path={stages["SUBMIT"].path}
                            component={Submit}
                        />
                        <Route
                            path={stages["AWAITING_INTERVIEW"].path}
                            component={InterviewNext}
                        />
                        <Route
                            path={stages["UNDER_REVIEW"].path}
                            component={ReviewingApplication}
                        />
                        <Route
                            path={stages["AWAITING_INFORMATION"].path}
                            component={ReviewingApplication}
                        />
                        <Route
                            path={stages["WAITING_LIST"].path}
                            component={ReviewingApplication}
                        />
                    </Switch>
                </ErrorBoundary>
            </Layout>
        </>
    );
};

export const submitGradesInterceptorModal = props => ({
    renderTitle: () => "Have you filled out all your grades? 👀",
    renderSubTitle: () => (
        <>
            <p>
                We use them to determine whether or not you're eligible to tutor
                with us, so it's important you complete this section accurately.
            </p>
            <p>You won't be able to come back to this page again!</p>
        </>
    ),
    renderActions: ({ closeModal }) => (
        <>
            <PrimaryCTA
                type="button"
                data-testid="submit-grades-button"
                onClick={() => {
                    props.submitForm();
                    return closeModal();
                }}
            >
                Submit my grades
            </PrimaryCTA>
            <SecondaryCTA
                data-testid="keep-editing-button"
                onClick={closeModal}
            >
                Keep editing
            </SecondaryCTA>
        </>
    ),
});

export default CanApplyContainer;
